import api from '@/lib/api';
import type { PaginatedResponse } from '@pms/shared';

export type { PaginatedResponse };

// ─── Rate Plan interfaces ────────────────────────────────────────────────────

export interface RatePlan {
  id: string;
  propertyId: string;
  name: string;
  code: string;
  isDefault: boolean;
  isActive: boolean;
  description: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateRatePlanPayload {
  propertyId: string;
  code: string;
  name: string;
  description?: string | undefined;
  isDefault?: boolean | undefined;
}

export interface UpdateRatePlanPayload {
  code?: string | undefined;
  name?: string | undefined;
  description?: string | undefined;
  isDefault?: boolean | undefined;
  isActive?: boolean | undefined;
}

export interface RatePlansQuery {
  propertyId: string;
  page?: number | undefined;
  limit?: number | undefined;
}

// ─── Room Type interfaces ────────────────────────────────────────────────────

export interface RoomType {
  id: string;
  propertyId: string;
  name: string;
  code: string;
  categoryId: string | null;
  maxOccupancy: number;
  description: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  category?: RoomTypeCategory | null | undefined;
}

export interface CreateRoomTypePayload {
  propertyId: string;
  code: string;
  name: string;
  categoryId?: string | undefined;
  maxOccupancy?: number | undefined;
  description?: string | undefined;
}

export interface UpdateRoomTypePayload {
  code?: string | undefined;
  name?: string | undefined;
  categoryId?: string | undefined;
  maxOccupancy?: number | undefined;
  description?: string | undefined;
  isActive?: boolean | undefined;
}

export interface RoomTypesQuery {
  propertyId?: string | undefined;
}

// ─── Room Type Category interfaces ───────────────────────────────────────────

export interface RoomTypeCategory {
  id: string;
  name: string;
  code: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateRoomTypeCategoryPayload {
  name: string;
  code: string;
}

export interface UpdateRoomTypeCategoryPayload {
  name?: string | undefined;
  code?: string | undefined;
}

// ─── Cancellation Policy interfaces ──────────────────────────────────────────

export interface CancellationRule {
  daysBeforeArrival: number;
  penaltyPercentage: number;
  description?: string;
}

export interface CancellationPolicy {
  id: string;
  propertyId: string;
  name: string;
  code: string;
  isDefault: boolean;
  description: string | null;
  rules: CancellationRule[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateCancellationPolicyPayload {
  propertyId: string;
  code: string;
  name: string;
  isDefault?: boolean | undefined;
  description?: string | undefined;
  rules?: CancellationRule[] | undefined;
}

export interface UpdateCancellationPolicyPayload {
  code?: string | undefined;
  name?: string | undefined;
  isDefault?: boolean | undefined;
  description?: string | undefined;
  rules?: CancellationRule[] | undefined;
}

export interface CancellationPoliciesQuery {
  propertyId: string;
}

// ─── Rate Plan API ───────────────────────────────────────────────────────────

export async function fetchRatePlans(query: RatePlansQuery): Promise<PaginatedResponse<RatePlan>> {
  const { data } = await api.get<PaginatedResponse<RatePlan>>('/rate-plans', { params: query });
  return data;
}

export async function createRatePlan(payload: CreateRatePlanPayload): Promise<RatePlan> {
  const { data } = await api.post('/rate-plans', payload);
  return data;
}

export async function fetchRatePlan(id: string): Promise<RatePlan> {
  const { data } = await api.get(`/rate-plans/${id}`);
  return data;
}

export async function updateRatePlan(id: string, payload: UpdateRatePlanPayload): Promise<RatePlan> {
  const { data } = await api.patch(`/rate-plans/${id}`, payload);
  return data;
}

export async function deleteRatePlan(id: string): Promise<void> {
  await api.delete(`/rate-plans/${id}`);
}

// ─── Room Types API ──────────────────────────────────────────────────────────

export async function fetchRoomTypes(query: RoomTypesQuery): Promise<PaginatedResponse<RoomType>> {
  const { data } = await api.get<PaginatedResponse<RoomType>>('/room-types', { params: query });
  return data;
}

export async function createRoomType(payload: CreateRoomTypePayload): Promise<RoomType> {
  const { data } = await api.post('/room-types', payload);
  return data;
}

export async function fetchRoomType(id: string): Promise<RoomType> {
  const { data } = await api.get(`/room-types/${id}`);
  return data;
}

export async function updateRoomType(id: string, payload: UpdateRoomTypePayload): Promise<RoomType> {
  const { data } = await api.patch(`/room-types/${id}`, payload);
  return data;
}

export async function deleteRoomType(id: string): Promise<void> {
  await api.delete(`/room-types/${id}`);
}

// ─── Room Type Categories API ────────────────────────────────────────────────

export async function fetchRoomTypeCategories(): Promise<PaginatedResponse<RoomTypeCategory>> {
  const { data } = await api.get<PaginatedResponse<RoomTypeCategory>>('/room-type-categories');
  return data;
}

export async function createRoomTypeCategory(payload: CreateRoomTypeCategoryPayload): Promise<RoomTypeCategory> {
  const { data } = await api.post('/room-type-categories', payload);
  return data;
}

export async function updateRoomTypeCategory(id: string, payload: UpdateRoomTypeCategoryPayload): Promise<RoomTypeCategory> {
  const { data } = await api.patch(`/room-type-categories/${id}`, payload);
  return data;
}

export async function deleteRoomTypeCategory(id: string): Promise<void> {
  await api.delete(`/room-type-categories/${id}`);
}

// ─── Cancellation Policies API ───────────────────────────────────────────────

export async function fetchCancellationPolicies(query: CancellationPoliciesQuery): Promise<PaginatedResponse<CancellationPolicy>> {
  const { data } = await api.get<PaginatedResponse<CancellationPolicy>>('/cancellation-policies', { params: query });
  return data;
}

export async function createCancellationPolicy(payload: CreateCancellationPolicyPayload): Promise<CancellationPolicy> {
  const { data } = await api.post('/cancellation-policies', payload);
  return data;
}

export async function fetchCancellationPolicy(id: string): Promise<CancellationPolicy> {
  const { data } = await api.get(`/cancellation-policies/${id}`);
  return data;
}

export async function updateCancellationPolicy(id: string, payload: UpdateCancellationPolicyPayload): Promise<CancellationPolicy> {
  const { data } = await api.patch(`/cancellation-policies/${id}`, payload);
  return data;
}

export async function deleteCancellationPolicy(id: string): Promise<void> {
  await api.delete(`/cancellation-policies/${id}`);
}
